import styled from "styled-components";
import useLecturePreProblem from "../../../hooks/leacture-detail/useLecturePreProblem";
import Modal from "../../../components/common/Modal";
import { useEffect, useRef, useState } from "react";

function LecturePreProblem({nav, onNavChangeHandler}){
    const canvasRef = useRef(null);
    const contextRef = useRef(null);
    const [ctx, setCtx] = useState();
    const [isDrawing, setIsDrawing] = useState(false);
    const [isErasing, setIsErasing] = useState(false);
    const [eraserSize, setEraserSize] = useState(10);
    const [penSize, setPenSize] = useState(2);
    const [penColor, setPenColor] = useState("#1d2340");
    const [elapsed, setElapsed] = useState(0);
    const [timing, setTiming] = useState(false);
    const historyRef = useRef([]);

    const {
        isOpenModal,
        modalProps,
        problem,
        canvasData,
        isSaving,
        saveCanvasData,
        fetchCanvasData,
        submitPreProblemSolveData
    } = useLecturePreProblem(nav, onNavChangeHandler);

    useEffect(() => {
        const canvas = canvasRef.current;
        const parent = canvas.parentElement;

        const resizeCanvas = () => {
            canvas.width = parent.clientWidth;
            canvas.height = parent.clientHeight;

            const context = canvas.getContext("2d");
            context.strokeStyle = "black";
            context.lineWidth = penSize;
            context.lineCap = "round";
            context.lineJoin = "round";
            contextRef.current = context;
            setCtx(context);
        };

        // 초기 실행
        resizeCanvas();

        // ResizeObserver 등록
        const resizeObserver = new ResizeObserver(resizeCanvas);
        resizeObserver.observe(parent);

        return () => resizeObserver.disconnect();
    }, []);

    // 캔버스 데이터 불러오기
    useEffect(() => {
        if (canvasData && ctx && canvasRef.current) {
            const canvas = canvasRef.current;
            const context = ctx;
            
            // 약간의 지연을 두어 컨텍스트가 완전히 준비되도록 함
            const renderCanvas = () => {
                if (canvasData.imageUrl) {
                    // Storage에서 불러온 이미지
                    const img = new Image();
                    img.crossOrigin = 'anonymous';
                    img.onload = () => {
                        context.clearRect(0, 0, canvas.width, canvas.height);
                        context.drawImage(img, 0, 0, canvas.width, canvas.height);
                    };
                    img.onerror = () => {
                        // 이미지 로드 실패 시 무시
                    };
                    img.src = canvasData.imageUrl;
                } else if (canvasData.canvasData) {
                    // Firestore에서 불러온 데이터 URL
                    if (!canvasData.canvasData.startsWith('data:image/')) {
                        return;
                    }
                    
                    const img = new Image();
                    img.crossOrigin = 'anonymous';
                    img.onload = () => {
                        context.clearRect(0, 0, canvas.width, canvas.height);
                        context.drawImage(img, 0, 0, canvas.width, canvas.height);
                    };
                    img.onerror = () => {
                        // 이미지 로드 실패 시 무시
                    };
                    img.src = canvasData.canvasData;
                }
            };

            // 다음 프레임에서 실행하여 컨텍스트가 완전히 준비되도록 함
            requestAnimationFrame(renderCanvas);
        }
    }, [canvasData, ctx]);

    // 페이지 로드시 캔버스 데이터 불러오기
    useEffect(() => {
        if (ctx && canvasRef.current) {
            fetchCanvasData();
        }
    }, [ctx]);

    useEffect(() => {
        if (!timing) return undefined;
        const timer = setInterval(() => setElapsed((value) => value + 1), 1000);
        return () => clearInterval(timer);
    }, [timing]);

    useEffect(() => {
        if (!ctx) return;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        if (isErasing) {
            ctx.globalCompositeOperation = "destination-out";
            ctx.globalAlpha = 1;
            ctx.lineWidth = eraserSize;
            return;
        }
        ctx.globalCompositeOperation = "source-over";
        ctx.strokeStyle = penColor;
        ctx.globalAlpha = penColor === "#ffe58a" ? 0.45 : 1;
        ctx.lineWidth = penSize;
    }, [ctx, isErasing, penColor, penSize, eraserSize]);

    // 캔버스 좌표 계산 헬퍼 함수
    const getCanvasCoordinates = (event) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        
        const clientX = event.clientX !== undefined ? event.clientX : event.touches?.[0]?.clientX;
        const clientY = event.clientY !== undefined ? event.clientY : event.touches?.[0]?.clientY;
        
        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY,
            pressure: event.pressure !== undefined ? event.pressure : 0.5
        };
    };

    const rememberCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        historyRef.current.push(canvas.toDataURL("image/png"));
        if (historyRef.current.length > 30) historyRef.current.shift();
    };

    const undoStroke = () => {
        const canvas = canvasRef.current;
        const snapshot = historyRef.current.pop();
        if (!canvas || !ctx || !snapshot) return;
        const img = new Image();
        img.onload = () => {
            ctx.save();
            ctx.globalCompositeOperation = "source-over";
            ctx.globalAlpha = 1;
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            ctx.restore();
        };
        img.src = snapshot;
    };

    const startDrawing = (event) => {
        event.preventDefault();
        if (!timing) setTiming(true);
        rememberCanvas();
        const coords = getCanvasCoordinates(event);
        setIsDrawing(true);
        if (ctx) {
            ctx.beginPath();
            ctx.moveTo(coords.x, coords.y);
            
            // 펜 압력에 따른 선 두께 조절 (펜인 경우에만)
            if (event.pointerType === 'pen' && coords.pressure > 0) {
                const baseSize = isErasing ? eraserSize : penSize;
                ctx.lineWidth = baseSize * (0.5 + coords.pressure * 0.5);
            }
        }
    };

    const draw = (event) => {
        if (!isDrawing || !ctx) return;
        event.preventDefault();
        const coords = getCanvasCoordinates(event);
        
        // 펜 압력에 따른 선 두께 조절 (펜인 경우에만)
        if (event.pointerType === 'pen' && coords.pressure > 0) {
            const baseSize = isErasing ? eraserSize : penSize;
            ctx.lineWidth = baseSize * (0.5 + coords.pressure * 0.5);
        }
        
        ctx.lineTo(coords.x, coords.y);
        ctx.stroke();
    };

    const stopDrawing = (event) => {
        if (event) {
            event.preventDefault();
        }
        setIsDrawing(false);
    };

    const toggleEraser = () => {
        if (!ctx) return;
        setIsErasing(prev => {
            const next = !prev;
            ctx.globalCompositeOperation = next ? "destination-out" : "source-over";
            ctx.lineWidth = next ? eraserSize : penSize;
            return next;
        });
    };


    const UpEraserSize = () => {
        if (!ctx) return;
        setEraserSize(prev => {
            const newSize = prev + 1;
            if (isErasing) {
                ctx.lineWidth = newSize;
            }
            return newSize;
        });
    }
    
    const DownEraserSize = () => {
        if (!ctx) return;
        setEraserSize(prev => {
            const newSize = Math.max(1, prev - 1);
            if (isErasing) {
                ctx.lineWidth = newSize;
            }
            return newSize;
        });
    }

    const UpPenSize = () => {
        if (!ctx) return;
        setPenSize(prev => {
            const newSize = prev + 1;
            if (!isErasing) {
                ctx.lineWidth = newSize;
            }
            return newSize;
        });
    }

    const DownPenSize = () => {
        if (!ctx) return;
        setPenSize(prev => {
            const newSize = Math.max(1, prev - 1);
            if (!isErasing) {
                ctx.lineWidth = newSize;
            }
            return newSize;
        });
    }

    const UpToolSze = () => {
        if(isErasing){
            UpEraserSize();
            return;
        }
        UpPenSize();
    }

    const DownToolSize = () => {
        if(isErasing){
            DownEraserSize();
            return;
        }
        DownPenSize();
    }

    // 캔버스 저장
    const handleSaveCanvas = async() => {
        if (!ctx) return;
        
        try {
            const canvas = canvasRef.current;
            const dataURL = canvas.toDataURL('image/png');
            await saveCanvasData(dataURL);
        } catch (error) {
            console.error('캔버스 저장 실패:', error);
        }
    }

    // 캔버스 지우기
    const handleClearCanvas = () => {
        if (!ctx) return;
        rememberCanvas();
        ctx.save();
        ctx.globalCompositeOperation = "source-over";
        ctx.globalAlpha = 1;
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.restore();
    }

    const clock = `${String(Math.floor(elapsed / 60)).padStart(2, "0")}:${String(elapsed % 60).padStart(2, "0")}`;
    const penColors = [
        { id: "ink", color: "#1d2340", label: "검정" },
        { id: "red", color: "#d9412b", label: "빨간펜" },
        { id: "mark", color: "#ffe58a", label: "형광펜" },
    ];

    // 제출 처리 (캔버스 자동 저장 포함)
    const handleSubmit = async() => {
        if (!ctx) {
            submitPreProblemSolveData();
            return;
        }
        
        try {
            const canvas = canvasRef.current;
            const dataURL = canvas.toDataURL('image/png');
            await submitPreProblemSolveData(dataURL);
        } catch (error) {
            // 캔버스 저장 실패해도 제출은 진행
            submitPreProblemSolveData();
        }
    }


    return(
        <Container>
            <Modal isOpen={isOpenModal} {...modalProps} children={modalProps.children}/>
            
            <StepHeading>
                <span>문제 풀이</span>
                <Timer type="button" onClick={() => setTiming((value) => !value)}>
                    {clock}{timing ? " · 진행" : " · 일시정지"}
                </Timer>
            </StepHeading>

            <ToolBar>
                <ToolGroup>
                    <ToggleModeButton
                        type="button"
                        onClick={() => { if (isErasing) toggleEraser(); }}
                        $active={!isErasing}
                    >
                        펜
                    </ToggleModeButton>
                    <ToggleModeButton
                        type="button"
                        onClick={() => { if (!isErasing) toggleEraser(); }}
                        $active={isErasing}
                    >
                        지우개
                    </ToggleModeButton>
                </ToolGroup>

                <ToolGroup>
                    {penColors.map((item) => (
                        <ColorButton
                            key={item.id}
                            type="button"
                            $color={item.color}
                            $active={!isErasing && penColor === item.color}
                            onClick={() => {
                                if (isErasing) toggleEraser();
                                setPenColor(item.color);
                            }}
                            aria-label={item.label}
                        />
                    ))}
                </ToolGroup>

                <ToolGroup>
                    <SizeControlButton type="button" onClick={DownToolSize}>−</SizeControlButton>
                    <SizeView>{isErasing ? eraserSize : penSize}</SizeView>
                    <SizeControlButton type="button" onClick={UpToolSze}>+</SizeControlButton>
                </ToolGroup>

                <ToolGroup>
                    <ActionButton type="button" onClick={undoStroke}>실행 취소</ActionButton>
                    <ActionButton type="button" onClick={handleClearCanvas}>전체 지우기</ActionButton>
                    <ActionButton type="button" onClick={handleSaveCanvas} disabled={isSaving}>
                        {isSaving ? "저장 중" : "임시 저장"}
                    </ActionButton>
                    <ActionButton type="button" $primary onClick={handleSubmit}>제출하기</ActionButton>
                </ToolGroup>
            </ToolBar>

            {/* 문제 영역 (위에 캔버스 오버레이) */}
            <ProblemList>
                {problem && problem.question ? (
                    <ProblemCard>
                        <Question>{problem.question}</Question>
                        <Content>{problem.content}</Content>
                    </ProblemCard>
                ) : (
                    <EmptyMessage>문제가 없습니다.</EmptyMessage>
                )}
                <Canvas 
                    ref={canvasRef}
                    onPointerDown={startDrawing}
                    onPointerUp={stopDrawing}
                    onPointerMove={draw}
                    onPointerLeave={stopDrawing}
                    onPointerCancel={stopDrawing}
                    touchAction="none"
                />
            </ProblemList>
        </Container>
    );
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    max-width: 782px;
    flex-shrink: 0;
    background: transparent;
`;

const StepHeading = styled.h2`
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    font-size: 1.25rem;
    font-weight: 700;
    letter-spacing: -0.03em;
    color: #1d2340;
`;

const ToolBar = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 20px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 12px rgba(0, 0, 0, 0.04);
    border: 1px solid #e2e8f0;
    flex-wrap: wrap;
`;

const ToolGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ToolLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
    margin-right: 4px;
`;

const ProblemList = styled.div`
    position: relative;
    background: white;
    border-radius: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 12px rgba(0, 0, 0, 0.04);
    border: 1px solid #e2e8f0;
    padding: 28px;
    min-height: 380px;
`;

const ProblemCard = styled.div`
    position: relative;
    z-index: 1;
`;

const Question = styled.div`
    font-weight: 700;
    font-size: 1.1rem;
    margin-bottom: 14px;
    color: #0f172a;
    line-height: 1.5;
`;

const Content = styled.div`
    color: #475569;
    white-space: pre-wrap;
    line-height: 1.7;
    font-size: 0.9375rem;
`;

const Answer = styled.div`
    color: #1d2340;
    font-size: 0.9375rem;
`;

const Label = styled.span`
    font-weight: 500;
`;

const EmptyMessage = styled.div`
    color: #94a3b8;
    text-align: center;
    padding: 40px 20px;
    font-size: 0.9375rem;
    font-weight: 500;
    position: relative;
    z-index: 1;
`;

const Canvas = styled.canvas`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: auto;
    z-index: 10;
    display: block;
    cursor: crosshair;
    touch-action: none;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
`;

const SizeView = styled.div`
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 700;
    background: #fff;
    border: 2px solid rgba(29, 35, 64, 0.16);
    color: #1d2340;
    min-width: 50px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const SizeControlButton = styled.button`
    width: 36px;
    height: 36px;
    border-radius: 8px;
    font-size: 20px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border: 2px solid rgba(29, 35, 64, 0.16);
    color: #1d2340;
    
    &:hover {
        background: rgba(29, 35, 64, 0.08);
        border-color: rgba(29, 35, 64, 0.28);
        transform: scale(1.05);
    }
    
    &:active {
        transform: scale(0.95);
    }
`;

const ToggleModeButton = styled.button`
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    background: ${({ $active }) => ($active ? "#ffe58a" : "#fff")};
    color: #1d2340;
    border: 1px solid ${({ $active }) => ($active ? "#ffe58a" : "#e7e1d3")};
`;

const ColorButton = styled.button`
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
    border: 2px solid ${({ $active }) => ($active ? "#1d2340" : "#fff")};
    box-shadow: 0 0 0 1px #e7e1d3;
    cursor: pointer;
`;

const Timer = styled.button`
    padding: 6px 10px;
    font-size: 13px;
    font-weight: 700;
    color: #1d2340;
    background: #fffbea;
    border: 1px solid #efe4bd;
    border-radius: 6px;
    cursor: pointer;
`;

const ActionButton = styled.button`
    padding: 10px 20px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: ${({ $primary }) => ($primary ? "#1d2340" : "#fff")};
    color: ${({ $primary }) => ($primary ? "#fff" : "#1d2340")};
    border: 1px solid ${({ $primary }) => ($primary ? "#1d2340" : "#e7e1d3")};
    
    &:hover {
        background: ${({ $primary }) => ($primary ? "#2c3561" : "#f8f5ee")};
    }

    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
    
    &:active {
        transform: translateY(0) scale(0.98);
    }
`

export default LecturePreProblem;